import { PropValidationMsg } from './propValidationMsg';


/**
 * Function signature for a property validator. This is a function that takes an object, and returns a list of
 * validation messages for its properties. Implementors can indicate no errors by returning an empty array.
 */
export type PropValidator<T> = (obj: T, extras?: any) => PropValidationMsg[];


/**
 * A rule for validating a single prop on an object and returning either a validation error or undefined.
 */
export type PropValidationRule<T> = (obj: T, extras?: any) => PropValidationMsg | undefined;


/**
 * Curried function for making object validators from a series of validation functions.
 *
 * @param rules
 * @example
 *     const validator = validateProps([
 *       person => isNilOrEmpty(person.firstName)
 *                   ? ({propName: 'firstName', msg: 'Person must have a first name'})
 *                   : undefined,
 *       person => ILLEGAL_CHARS.any(char => person.firstName.includes(char))
 *                   ? ({propName: 'firstName', msg: `First name cannot have chars: ${JSON.stringify(ILLEGAL_CHARS)}`})
 *                   : undefined,
 *       person => isNilOrEmpty(person.lastName)
 *                   ? ({propName: 'lastName', msg: 'Person must have a first name'})
 *                   : undefined,
 *       person => ILLEGAL_CHARS.any(char => person.lastName.includes(char))
 *                   ? ({propName: 'lastName', msg: `First name cannot have chars: ${JSON.stringify(ILLEGAL_CHARS)}`})
 *                   : undefined,
 *     ]);
 *     const errors = validator(maPerson); // Second arg lets you pass extras e.g. a list of taken usernames
 */
export const validateProps = <T>(
  rules: Array<PropValidationRule<T>>,
): PropValidator<T> => (
  obj: T,
  extras?: any,
): PropValidationMsg[] => rules.reduce(
  (running, next) => {
    const validation = next(obj, extras);
    return validation ? running.concat(validation) : running;
  },
  [] as PropValidationMsg[],
);


/**
 * Sugar for making a PropValidationRule that just runs a predicate and shows a message if the predicate says the object
 * is invalid.
 */
export const makeRule = <T>(
  isInvalid: (obj: T, extras?: any) => boolean,
  invalidMessage: (extras?: any) => string,
  propName: string,
): PropValidationRule<T> => (obj: T, extras?: any) => isInvalid(obj, extras)
                                                      ? { propName: propName, msg: invalidMessage(extras) }
                                                      : undefined;
